import type { CoasterProject } from '../schema';
import type { CoasterPlatformAdapter, FixtureLaunchRequest, LaunchResolution } from './adapter';
import type {
  CloudDraftRecord,
  CloudDraftSummary,
  CatalogHoverUploadDescriptor,
  CatalogHoverVideoAsset,
  CoasterViewer,
  HeroUploadDescriptor,
  HeroAssetBundle,
  MyCoasterSummary,
  PublicCatalogPage,
  PublicCatalogQuery,
  PublicCoasterPayload,
  PublicCoasterRecord,
  PublicCoasterViewerStateBatch,
  PublishRequest,
  PublishStatusRecord,
  ReadOnlyShare,
  ReadOnlyShareRecord,
  RemixRequest,
  RemixResult,
  ReferralLinkResult,
  QualifiedRideCompletionRequest,
  QualifiedRideCompletionResult,
  QualifiedRideStartRequest,
  QualifiedRideStartResult,
  SaveCloudDraftOptions,
  SocialMutationRequest,
  SocialMutationResult,
  BunchaWindowApi,
} from './contracts';
import { COASTERS_ASSERTION_AUDIENCE } from './contracts';
import { hydratePublicCoaster } from './public-model';
import {
  type PlatformCapabilityId,
  type PlatformCapabilityState,
  type PlatformErrorCode,
  type PlatformResult,
  unavailable,
} from './capabilities';

export interface RemotePhase4PlatformAdapterOptions {
  apiBaseUrl: string;
  buncha?: BunchaWindowApi;
  fetch?: typeof fetch;
  timeoutMs?: number;
}

const remoteStates = {
  anonymous_rides: {
    id: 'anonymous_rides',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Anonymous rides are available.',
  },
  fixture_launch: {
    id: 'fixture_launch',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Fixture launch IDs are available.',
  },
  canvas_capture: {
    id: 'canvas_capture',
    availability: 'available',
    phaseGate: 'phase_1',
    reason: 'Canvas capture is available.',
  },
  cloud_drafts: {
    id: 'cloud_drafts',
    availability: 'unavailable',
    phaseGate: 'phase_4',
    reason: 'Authenticated cloud drafts await the approved Buncha Games assertion contract.',
  },
  publishing: {
    id: 'publishing',
    availability: 'unavailable',
    phaseGate: 'phase_4',
    reason: 'Moderated publishing is not deployed.',
  },
  catalog_hover_upload: {
    id: 'catalog_hover_upload',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'The approved Buncha Games motion preview upload hook has not been supplied.',
  },
  public_catalog: {
    id: 'public_catalog',
    availability: 'unavailable',
    phaseGate: 'phase_4',
    reason: 'The production public coaster catalog is not deployed.',
  },
  read_only_shares: {
    id: 'read_only_shares',
    availability: 'unavailable',
    phaseGate: 'phase_4',
    reason: 'Production read-only links are not deployed.',
  },
  remixing: {
    id: 'remixing',
    availability: 'unavailable',
    phaseGate: 'phase_4',
    reason: 'Production remixing is not deployed.',
  },
  social_writes: {
    id: 'social_writes',
    availability: 'unavailable',
    phaseGate: 'phase_7',
    reason: 'Boost and favorite writes are not enabled.',
  },
  camera: {
    id: 'camera',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Camera permission is not enabled.',
  },
  microphone: {
    id: 'microphone',
    availability: 'unavailable',
    phaseGate: 'phase_6',
    reason: 'Microphone permission is not enabled.',
  },
  referrals: {
    id: 'referrals',
    availability: 'unavailable',
    phaseGate: 'phase_7',
    reason: 'Referral attribution is not enabled.',
  },
  credits: {
    id: 'credits',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Buncha Credits are not enabled.',
  },
  iap: {
    id: 'iap',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Purchases are not enabled.',
  },
  rewarded_ads: {
    id: 'rewarded_ads',
    availability: 'unavailable',
    phaseGate: 'phase_8',
    reason: 'Rewarded ads are not enabled.',
  },
} as const satisfies Record<PlatformCapabilityId, PlatformCapabilityState>;

function platformError(
  code: PlatformErrorCode,
  message: string,
  options: { retryable?: boolean; details?: Readonly<Record<string, unknown>> } = {},
): PlatformResult<never> {
  return {
    ok: false,
    error: {
      code,
      message,
      recoverable: true,
      retryable: options.retryable ?? false,
      ...(options.details ? { details: options.details } : {}),
    },
  };
}

function errorCodeForStatus(status: number): PlatformErrorCode {
  if (status === 400) return 'invalid_request';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409 || status === 412) return 'conflict';
  if (status === 422) return 'validation_failed';
  return 'platform_error';
}

export class RemotePhase4PlatformAdapter implements CoasterPlatformAdapter {
  readonly capabilities = remoteStates;
  private readonly apiBaseUrl: string;
  private readonly buncha: BunchaWindowApi | undefined;
  private readonly fetcher: typeof fetch;
  private readonly timeoutMs: number;

  constructor(options: RemotePhase4PlatformAdapterOptions) {
    this.apiBaseUrl = options.apiBaseUrl.replace(/\/+$/, '');
    this.buncha = options.buncha ?? (typeof window === 'undefined' ? undefined : window.buncha);
    this.fetcher = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.timeoutMs = Math.min(Math.max(options.timeoutMs ?? 8_000, 250), 30_000);
  }

  getCapability(id: PlatformCapabilityId): PlatformCapabilityState {
    if (
      (id === 'cloud_drafts' ||
        id === 'publishing' ||
        id === 'read_only_shares' ||
        id === 'remixing') &&
      !this.buncha?.player
    )
      return {
        ...this.capabilities[id],
        availability: 'unavailable',
        reason: 'Sign in through Buncha Games to use this feature.',
      };
    return this.capabilities[id];
  }

  async getViewer(): Promise<PlatformResult<CoasterViewer>> {
    if (!this.buncha?.player)
      return { ok: true, value: { kind: 'anonymous', id: null, displayName: 'Guest' } };
    try {
      const profile = await this.withTimeout(this.buncha.player.getProfile());
      if (!profile.available || !profile.player)
        return { ok: true, value: { kind: 'anonymous', id: null, displayName: 'Guest' } };
      return {
        ok: true,
        value: {
          kind: 'authenticated',
          id: profile.player.id,
          displayName: profile.player.displayName,
          avatarUrl: profile.player.avatarUrl ?? null,
        },
      };
    } catch (error) {
      return this.caught(error, 'Could not read the Buncha Games player profile.');
    }
  }

  async resolveFixtureLaunch(
    request: FixtureLaunchRequest,
  ): Promise<PlatformResult<LaunchResolution>> {
    if (!request.coasterId) return { ok: true, value: { kind: 'blank' } };
    if (request.coasterId.startsWith('fixture.'))
      return { ok: true, value: { kind: 'fixture', fixtureId: request.coasterId } };
    return platformError('not_found', 'The requested fixture does not exist.');
  }

  async listCloudDrafts(): Promise<PlatformResult<readonly CloudDraftSummary[]>> {
    return this.request('/v1/projects', { auth: 'required' });
  }

  async loadCloudDraft(projectId: string): Promise<PlatformResult<CloudDraftRecord>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}`, { auth: 'required' });
  }

  async saveCloudDraft(
    project: CoasterProject,
    options: SaveCloudDraftOptions = {},
  ): Promise<PlatformResult<CloudDraftRecord>> {
    return this.request(`/v1/projects/${encodeURIComponent(project.projectId)}`, {
      method: 'PUT',
      auth: 'required',
      body: { project, expectedVersion: options.expectedVersion ?? null },
    });
  }

  async deleteCloudDraft(
    projectId: string,
    expectedVersion: number,
  ): Promise<PlatformResult<null>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      auth: 'required',
      body: { expectedVersion },
    });
  }

  async requestPublish(request: PublishRequest): Promise<PlatformResult<PublishStatusRecord>> {
    return this.request(`/v1/projects/${encodeURIComponent(request.projectId)}/publish`, {
      method: 'POST',
      auth: 'required',
      body: request,
    });
  }

  async getPublishStatus(projectId: string): Promise<PlatformResult<PublishStatusRecord>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}/publish-status`, {
      auth: 'required',
    });
  }

  async requestHeroUpload(projectId: string): Promise<PlatformResult<HeroUploadDescriptor>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}/hero-upload`, {
      method: 'POST',
      auth: 'required',
      body: {},
    });
  }

  async uploadHeroAssets(
    projectId: string,
    assets: HeroAssetBundle,
  ): Promise<PlatformResult<HeroUploadDescriptor>> {
    const descriptor = await this.requestHeroUpload(projectId);
    if (!descriptor.ok) return descriptor;
    const pairs = [
      [descriptor.value.assets.hero16x9, assets.hero16x9],
      [descriptor.value.assets.openGraph, assets.openGraph],
    ] as const;
    for (const [target, blob] of pairs) {
      if (!target.acceptedContentTypes.includes(blob.type as 'image/webp' | 'image/png'))
        return platformError('validation_failed', 'Hero images must be WebP or PNG files.');
      if (blob.size > target.maxBytes)
        return platformError('validation_failed', 'A hero image exceeds the upload size limit.');
      const uploaded = await this.uploadBlob(target.uploadUrl, blob);
      if (!uploaded.ok) return uploaded;
    }
    return descriptor;
  }

  async uploadCatalogHoverVideo(
    _projectId: string,
    _asset: CatalogHoverVideoAsset,
    _idempotencyKey: string,
  ): Promise<PlatformResult<CatalogHoverUploadDescriptor>> {
    return unavailable(this.capabilities.catalog_hover_upload);
  }

  async getPublicCoaster(reference: {
    publicCoasterId?: string;
    publicSlug?: string;
  }): Promise<PlatformResult<PublicCoasterRecord>> {
    if (this.getCapability('public_catalog').availability !== 'available')
      return unavailable(this.capabilities.public_catalog);
    let payload: PlatformResult<PublicCoasterPayload>;
    if (reference.publicCoasterId)
      payload = await this.request(
        `/v1/public/coasters/${encodeURIComponent(reference.publicCoasterId)}`,
      );
    else if (reference.publicSlug)
      payload = await this.request(
        `/v1/public/coasters/slug/${encodeURIComponent(reference.publicSlug)}`,
      );
    else return platformError('invalid_request', 'A public coaster ID or slug is required.');
    return payload.ok ? { ok: true, value: hydratePublicCoaster(payload.value) } : payload;
  }

  async listPublicCoasters(query: PublicCatalogQuery): Promise<PlatformResult<PublicCatalogPage>> {
    if (this.getCapability('public_catalog').availability !== 'available')
      return unavailable(this.capabilities.public_catalog);
    const params = new URLSearchParams({
      sort: query.sort,
      remixable: String(query.remixableOnly),
      limit: String(Math.min(Math.max(query.limit ?? 18, 1), 18)),
    });
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.creatorId) params.set('creatorId', query.creatorId);
    return this.request(`/v1/public/coasters?${params.toString()}`);
  }

  async getViewerCoasterStates(
    publicCoasterIds: readonly string[],
  ): Promise<PlatformResult<PublicCoasterViewerStateBatch>> {
    if (this.getCapability('social_writes').availability !== 'available')
      return unavailable(this.capabilities.social_writes);
    return this.request('/v1/viewer/coasters/state', {
      method: 'POST',
      auth: 'required',
      body: { publicCoasterIds: [...publicCoasterIds] },
    });
  }

  async listFavoriteCoasters(
    query: PublicCatalogQuery,
  ): Promise<PlatformResult<PublicCatalogPage>> {
    if (this.getCapability('social_writes').availability !== 'available')
      return unavailable(this.capabilities.social_writes);
    const params = new URLSearchParams({
      sort: query.sort,
      remixable: String(query.remixableOnly),
      limit: String(Math.min(Math.max(query.limit ?? 18, 1), 18)),
    });
    if (query.cursor) params.set('cursor', query.cursor);
    return this.request(`/v1/me/favorites?${params.toString()}`, { auth: 'required' });
  }

  async listMyCoasters(): Promise<PlatformResult<readonly MyCoasterSummary[]>> {
    if (this.getCapability('cloud_drafts').availability !== 'available')
      return unavailable(this.capabilities.cloud_drafts);
    return this.request('/v1/me/coasters', { auth: 'required' });
  }

  async setBoost(request: SocialMutationRequest): Promise<PlatformResult<SocialMutationResult>> {
    void request;
    return unavailable(this.capabilities.social_writes);
  }

  async setFavorite(request: SocialMutationRequest): Promise<PlatformResult<SocialMutationResult>> {
    void request;
    return unavailable(this.capabilities.social_writes);
  }

  async createReferralLink(publicCoasterId: string): Promise<PlatformResult<ReferralLinkResult>> {
    void publicCoasterId;
    return unavailable(this.capabilities.referrals);
  }

  async startQualifiedRide(
    request: QualifiedRideStartRequest,
  ): Promise<PlatformResult<QualifiedRideStartResult>> {
    void request;
    return unavailable(this.capabilities.referrals);
  }

  async completeQualifiedRide(
    request: QualifiedRideCompletionRequest,
  ): Promise<PlatformResult<QualifiedRideCompletionResult>> {
    void request;
    return unavailable(this.capabilities.referrals);
  }

  async createReadOnlyShare(projectId: string): Promise<PlatformResult<ReadOnlyShare>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}/shares`, {
      method: 'POST',
      auth: 'required',
      body: {},
    });
  }

  async revokeReadOnlyShare(shareId: string): Promise<PlatformResult<null>> {
    return this.request(`/v1/shares/${encodeURIComponent(shareId)}`, {
      method: 'DELETE',
      auth: 'required',
    });
  }

  async loadReadOnlyShare(
    shareId: string,
    secret: string,
  ): Promise<PlatformResult<ReadOnlyShareRecord>> {
    return this.request(`/v1/shares/${encodeURIComponent(shareId)}`, {
      headers: { 'X-Coaster-Share-Secret': secret },
    });
  }

  async remixCoaster(request: RemixRequest): Promise<PlatformResult<RemixResult>> {
    const headers = request.share ? { 'X-Coaster-Share-Secret': request.share.secret } : undefined;
    const body = request.share
      ? { shareId: request.share.shareId }
      : { publicCoasterId: request.publicCoasterId };
    return this.request('/v1/remixes', {
      method: 'POST',
      auth: 'required',
      headers,
      body,
    });
  }

  async publish(projectId: string, revisionId: string): Promise<PlatformResult<never>> {
    return this.request(`/v1/projects/${encodeURIComponent(projectId)}/publish`, {
      method: 'POST',
      auth: 'required',
      body: { projectId, revisionId },
    });
  }

  async requestCamera(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.camera);
  }
  async requestMicrophone(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.microphone);
  }
  async attributeReferral(_token: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.referrals);
  }
  async getCreditBalance(): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.credits);
  }
  async purchase(_sku: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.iap);
  }
  async showRewardedAd(_placement: string): Promise<PlatformResult<never>> {
    return unavailable(this.capabilities.rewarded_ads);
  }

  private async request<T>(
    path: string,
    options: {
      method?: string;
      auth?: 'required' | 'none';
      body?: unknown;
      headers?: Record<string, string>;
    } = {},
  ): Promise<PlatformResult<T>> {
    const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.auth === 'required') {
      const assertion = await this.getAssertion();
      if (!assertion.ok) return assertion;
      headers.Authorization = `Bearer ${assertion.value}`;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetcher(`${this.apiBaseUrl}${path}`, {
        method: options.method ?? 'GET',
        credentials: 'omit',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: controller.signal,
      });
      const payload = (await response.json().catch(() => null)) as unknown;
      if (!response.ok) {
        const object =
          payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
        return platformError(
          errorCodeForStatus(response.status),
          typeof object.message === 'string'
            ? object.message
            : `Coaster service request failed (${response.status}).`,
          { retryable: response.status >= 500 || response.status === 429, details: object },
        );
      }
      return { ok: true, value: payload as T };
    } catch (error) {
      return this.caught(error, 'Could not reach the coaster service.');
    } finally {
      clearTimeout(timeout);
    }
  }

  private async getAssertion(): Promise<PlatformResult<string>> {
    if (!this.buncha?.player)
      return platformError('unauthenticated', 'Sign in through Buncha Games to save or publish.');
    try {
      const assertion = await this.withTimeout(
        this.buncha.player.getAssertion({ audience: COASTERS_ASSERTION_AUDIENCE }),
      );
      if (!assertion.available || !assertion.token)
        return platformError(
          'unauthenticated',
          assertion.reason ?? 'A Buncha Games player assertion is unavailable.',
        );
      return { ok: true, value: assertion.token };
    } catch (error) {
      return this.caught(error, 'Could not verify the signed-in Buncha Games player.');
    }
  }

  private async uploadBlob(url: string, blob: Blob): Promise<PlatformResult<null>> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetcher(url, {
        method: 'PUT',
        credentials: 'omit',
        headers: { 'Content-Type': blob.type },
        body: blob,
        signal: controller.signal,
      });
      if (!response.ok)
        return platformError('platform_error', `Hero upload failed (${response.status}).`, {
          retryable: response.status >= 500 || response.status === 429,
        });
      return { ok: true, value: null };
    } catch (error) {
      return this.caught(error, 'Could not upload the coaster hero image.');
    } finally {
      clearTimeout(timeout);
    }
  }

  private async withTimeout<T>(promise: Promise<T>): Promise<T> {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_resolve, reject) => {
          timeout = setTimeout(
            () => reject(new DOMException('Timed out', 'AbortError')),
            this.timeoutMs,
          );
        }),
      ]);
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
    }
  }

  private caught(error: unknown, fallback: string): PlatformResult<never> {
    if (error instanceof DOMException && error.name === 'AbortError')
      return platformError('timeout', 'The coaster service took too long to respond.', {
        retryable: true,
      });
    return platformError('platform_error', fallback, {
      retryable: true,
    });
  }
}

export function createRemotePhase4PlatformAdapter(
  options: RemotePhase4PlatformAdapterOptions,
): CoasterPlatformAdapter {
  return new RemotePhase4PlatformAdapter(options);
}
